import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Trash2, Upload, FolderOpen, Music, Video, Image as ImageIcon, GripVertical, Loader2, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { MediaPicker } from "./MediaPicker";

type TalentMedia = {
  id: string;
  talent_id: string;
  media_type: "audio" | "video" | "image";
  url: string;
  title: string | null;
  display_order: number;
};

interface MediaPortfolioManagerProps {
  talentId: string;
}

export const MediaPortfolioManager = ({ talentId }: MediaPortfolioManagerProps) => {
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [mediaType, setMediaType] = useState<"audio" | "video" | "image">("audio");
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [isUploading, setIsUploading] = useState(false);
const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: mediaItems, isLoading } = useQuery({
    queryKey: ["talent-media", talentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("talent_media")
        .select("*")
        .eq("talent_id", talentId)
        .order("display_order");
      if (error) throw error;
      return data as TalentMedia[];
    },
    enabled: !!talentId,
  });

  const addMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("talent_media").insert({
        talent_id: talentId,
        media_type: mediaType,
        url,
        title: title || null,
        display_order: (mediaItems?.length || 0) + 1,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["talent-media", talentId] });
      setIsAdding(false);
      setUrl("");
      setTitle("");
      toast.success("Media added");
    },
    onError: (error) => toast.error("Failed: " + error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("talent_media").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["talent-media", talentId] });
      toast.success("Media removed");
    },
    onError: (error) => toast.error("Failed: " + error.message),
  });

  const updateTitleMutation = useMutation({
    mutationFn: async ({ id, title }: { id: string; title: string }) => {
      const { error } = await supabase.from("talent_media").update({ title: title || null }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["talent-media", talentId] });
      setEditingId(null);
      toast.success("Title updated");
    },
    onError: (error) => toast.error("Failed: " + error.message),
  });

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const fileExt = file.name.split(".").pop();
      const folder = mediaType === "audio" ? "audio" : mediaType === "video" ? "video" : "images";
      const fileName = `${folder}/${talentId}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("demos")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("demos")
        .getPublicUrl(fileName);

      setUrl(publicUrl);
      toast.success("File uploaded");
    } catch (error: any) {
      toast.error("Upload failed: " + error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const getMediaIcon = (type: string) => {
    switch (type) {
      case "audio": return <Music size={16} className="text-blue-500" />;
      case "video": return <Video size={16} className="text-purple-500" />;
      case "image": return <ImageIcon size={16} className="text-green-500" />;
      default: return null;
    }
  };

  const getAcceptType = () => {
    switch (mediaType) {
      case "audio": return "audio/*";
      case "video": return "video/*";
      case "image": return "image/*";
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-medium">Media Portfolio</Label>
        {!isAdding && (
          <Button type="button" variant="outline" size="sm" onClick={() => setIsAdding(true)}>
            <Plus size={14} className="mr-1" />
            Add Media
          </Button>
        )}
      </div>

      {isAdding && (
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={mediaType} onValueChange={(v) => setMediaType(v as any)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="audio">Audio</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="image">Image</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Title (optional)</Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Demo Reel"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>URL</Label>
              {isUploading ? (
                <div className="flex items-center justify-center gap-3 p-6 border-2 border-dashed border-primary/30 rounded-lg bg-primary/5 animate-pulse">
                  <Loader2 size={20} className="animate-spin text-primary" />
                  <span className="text-sm text-muted-foreground">Uploading file...</span>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://... or upload a file"
                    className="flex-1"
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={getAcceptType()}
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(file);
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload size={14} className="mr-1" />
                    Upload
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsMediaPickerOpen(true)}
                  >
                    <FolderOpen size={14} className="mr-1" />
                    Browse
                  </Button>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setIsAdding(false);
                  setUrl("");
                  setTitle("");
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => addMutation.mutate()}
                disabled={!url || addMutation.isPending}
              >
                Add
              </Button>
            </div>

            <MediaPicker
              open={isMediaPickerOpen}
              onOpenChange={setIsMediaPickerOpen}
              onSelect={(selectedUrl) => setUrl(selectedUrl)}
              bucket="demos"
              folder={mediaType === "audio" ? "audio" : mediaType === "video" ? "video" : "images"}
              mediaType={mediaType}
            />
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : mediaItems && mediaItems.length > 0 ? (
        <div className="space-y-2">
          {mediaItems.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
            >
              <GripVertical size={14} className="text-muted-foreground" />
              {getMediaIcon(item.media_type)}
              <div className="flex-1 min-w-0">
                {editingId === item.id ? (
                  <div className="flex items-center gap-1">
                    <Input
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      className="h-7 text-sm"
                      placeholder="Title"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") updateTitleMutation.mutate({ id: item.id, title: editingTitle });
                        if (e.key === "Escape") setEditingId(null);
                      }}
                    />
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => updateTitleMutation.mutate({ id: item.id, title: editingTitle })} disabled={updateTitleMutation.isPending}>
                      <Check size={12} />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => setEditingId(null)}>
                      <X size={12} />
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm font-medium truncate">
                    {item.title || item.url.split("/").pop()}
                  </p>
                )}
                <p className="text-xs text-muted-foreground capitalize">
                  {item.media_type}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => { setEditingId(item.id); setEditingTitle(item.title || ""); }}
              >
                <Pencil size={14} />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => deleteMutation.mutate(item.id)}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No media added yet.</p>
      )}
    </div>
  );
};
